<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { SemesterListItem } from '@/types'

const { t } = useI18n()
const { breadcrumbs, showSemesterSelector } = useBreadcrumbs()
const { selectedSemester, availableSemesters, selectSemester, isLoading } = useMetadata()

type PopoverRef = {
  toggle: (event: Event) => void
  hide: () => void
}

const semesterPopover = ref<PopoverRef | null>(null)
const breadcrumbItems = computed(() => [
  {
    label: breadcrumbs.value.length ? undefined : t('router.pages.home'),
    icon: 'i-lucide-house',
    to: '/',
  },
  ...breadcrumbs.value.map((item) => ({
    label: item.isTranslated ? item.label : t(item.label),
    icon: item.icon,
    to: item.route && !item.isCurrent ? item.route : undefined,
    active: item.isCurrent,
    // Nuxt UI marks the current crumb with `text-[var(--app-text-primary)]`; KIT green is only 3.54:1 on the page
    // background, so the current crumb uses the per-theme primary text step instead.
    class: item.isCurrent ? 'text-[var(--app-text-primary)]' : undefined,
  })),
])

function toggleSemesterPopover(event: Event) {
  semesterPopover.value?.toggle(event)
}

function handleSemesterSelect(semester: SemesterListItem) {
  selectSemester(semester)
  semesterPopover.value?.hide()
}
</script>

<template>
  <div class="flex min-h-9 items-center gap-2 py-1 md:min-h-10 md:gap-4 md:py-1.5">
    <UBreadcrumb :items="breadcrumbItems" class="bg-transparent! pr-0!" />
    <div v-if="showSemesterSelector" class="flex items-center gap-2">
      <UIcon name="i-lucide-chevron-right" class="text-muted" />
      <button
        class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm font-semibold text-gray-700 transition-colors hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-neutral-800"
        :disabled="isLoading"
        @click="toggleSemesterPopover"
      >
        <span v-if="selectedSemester" class="font-medium">
          {{ selectedSemester.nameEN }}
        </span>
        <span v-else class="text-muted">
          {{ t('common.terms.selectSemester') }}
        </span>
        <UIcon name="i-lucide-chevron-down" class="text-xs" />
      </button>

      <AppPopover ref="semesterPopover">
        <div class="min-w-[200px] p-2">
          <div class="mb-2 px-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {{ t('common.terms.selectSemester') }}
          </div>
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <UIcon name="i-lucide-loader-circle" class="animate-spin" />
            <span class="ml-2 text-sm">{{ t('common.labels.loading') }}</span>
          </div>
          <div v-else class="space-y-1">
            <button
              v-for="semester in availableSemesters"
              :key="semester.id"
              class="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
              :class="{
                'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300':
                  selectedSemester?.id === semester.id,
                'font-semibold': semester.isDefault,
              }"
              @click="handleSemesterSelect(semester)"
            >
              <span>{{ semester.nameEN }}</span>
              <div class="flex items-center gap-1">
                <UIcon
                  name="i-lucide-check"
                  v-if="selectedSemester?.id === semester.id"
                  class="text-xs text-[var(--app-text-primary)]"
                />
              </div>
            </button>
          </div>
        </div>
      </AppPopover>
    </div>
  </div>
</template>
